import { format, isToday, parseISO, subDays } from 'date-fns'
import { ArrowRight, Download, Eye, FileClock, Globe, Laptop, ScrollText, ShieldAlert, UserRound, Users } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { PageHeader } from '@/components/common/PageHeader'
import { Pager } from '@/components/common/Pager'
import { EmptyState, ErrorState } from '@/components/common/States'
import { usePagination } from '@/components/common/usePagination'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip } from '@/components/ui/tooltip'
import { downloadCsv } from '@/features/finance/csv'
import { Kpi, Section } from '@/features/finance/components/FinanceBits'
import { cn } from '@/lib/utils'
import { fmtDate, fmtDateTime } from '../shared/format'
import { useAuditLog } from './api'
import { MetaList, Toolbar } from './components/AdminBits'
import type { AuditEntry, AuditModule, AuditSeverity } from './types'
import { includesTr } from './ui'

const MODULES: AuditModule[] = ['auth', 'access', 'crm', 'finance', 'tax', 'hr', 'licensing', 'catalog', 'infra', 'mail']
const SEVERITY_TONE: Record<AuditSeverity, BadgeTone> = { info: 'neutral', notice: 'warning', critical: 'danger' }
const SEVERITY_BAR: Record<AuditSeverity, string> = { info: 'bg-muted-foreground/30', notice: 'bg-warning', critical: 'bg-destructive' }

export function AuditPage() {
  const { t } = useTranslation()
  const list = useAuditLog()
  const [q, setQ] = useState('')
  const [module, setModule] = useState<AuditModule | 'all'>('all')
  const [severity, setSeverity] = useState<AuditSeverity | 'all'>('all')
  const [actor, setActor] = useState('all')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const all = list.data ?? []

  const actors = [...new Set(all.map((a) => a.actor.email))]
  const rows = all.filter(
    (a) =>
      (module === 'all' || a.module === module) &&
      (severity === 'all' || a.severity === severity) &&
      (actor === 'all' || a.actor.email === actor) &&
      includesTr(`${a.target} ${a.summary} ${a.actor.name} ${a.ip}`, q),
  )
  const { pageRows, pager } = usePagination(rows, [q, module, severity, actor])
  const groups = new Map<string, AuditEntry[]>()
  for (const a of pageRows) groups.set(a.at.slice(0, 10), [...(groups.get(a.at.slice(0, 10)) ?? []), a])
  const selected = rows.find((a) => a.id === selectedId) ?? rows[0] ?? null
  const weekAgo = subDays(new Date(), 7)

  const exportCsv = () =>
    downloadCsv(
      'denetim-kayitlari.csv',
      [t('admin.audit.columns.at'), t('admin.audit.columns.actor'), t('admin.audit.columns.module'), t('admin.audit.columns.action'), t('admin.audit.columns.target'), t('admin.audit.columns.summary'), 'IP'],
      rows.map((a) => [fmtDateTime(a.at), a.actor.email, t(`admin.audit.modules.${a.module}`), t(`admin.audit.actions.${a.action}`), a.target, a.summary, a.ip]),
    )

  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.admin')}
        title={t('admin.audit.title')}
        description={t('admin.audit.subtitle')}
        actions={
          <Button variant="outline" onClick={exportCsv} disabled={!rows.length}>
            <Download />
            {t('admin.common.exportCsv')}
          </Button>
        }
      />

      {list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : !list.data ? (
        <Skeleton className="h-96 rounded-xl" />
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Kpi icon={FileClock} tone="primary" label={t('admin.audit.kpi.today')} value={all.filter((a) => isToday(parseISO(a.at))).length} footer={t('admin.audit.kpi.todayHint', { count: all.length })} />
            <Kpi
              icon={ShieldAlert}
              tone="danger"
              label={t('admin.audit.kpi.critical')}
              value={all.filter((a) => a.severity === 'critical' && parseISO(a.at) >= weekAgo).length}
              footer={
                <button type="button" className="underline-offset-2 hover:underline" onClick={() => setSeverity('critical')}>
                  {t('admin.audit.kpi.criticalHint')}
                </button>
              }
            />
            <Kpi icon={Users} tone="info" label={t('admin.audit.kpi.actors')} value={actors.length} footer={t('admin.audit.kpi.actorsHint')} />
            <Kpi icon={Download} tone="warning" label={t('admin.audit.kpi.exports')} value={all.filter((a) => a.action === 'export' || a.action === 'reveal').length} footer={t('admin.audit.kpi.exportsHint')} />
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_24rem]">
            <section className="min-w-0 overflow-hidden rounded-2xl border bg-card shadow-xs">
              <Toolbar q={q} onQ={setQ} placeholder={t('admin.audit.search')} count={t('admin.common.count', { count: rows.length })}>
                <Select
                  className="w-40"
                  value={module}
                  onChange={setModule}
                  aria-label={t('admin.audit.columns.module')}
                  options={[{ value: 'all', label: t('admin.audit.allModules') }, ...MODULES.map((m) => ({ value: m, label: t(`admin.audit.modules.${m}`) }))]}
                />
                <Select
                  className="w-36"
                  value={severity}
                  onChange={setSeverity}
                  aria-label={t('admin.audit.severity')}
                  options={[{ value: 'all', label: t('admin.audit.allSeverities') }, ...(['info', 'notice', 'critical'] as const).map((s) => ({ value: s, label: t(`admin.audit.severities.${s}`) }))]}
                />
                <Select
                  className="w-48"
                  value={actor}
                  onChange={setActor}
                  aria-label={t('admin.audit.columns.actor')}
                  options={[{ value: 'all', label: t('admin.audit.allActors') }, ...actors.map((e) => ({ value: e, label: e }))]}
                />
              </Toolbar>
              {rows.length === 0 ? (
                <EmptyState icon={ScrollText} title={t('admin.common.noMatch')} />
              ) : (
                <div>
                  {[...groups].map(([day, items]) => (
                    <div key={day}>
                      <p className="sticky top-0 z-[1] border-b bg-muted/60 px-4 py-1.5 text-xs font-semibold text-muted-foreground backdrop-blur">{fmtDate(day)}</p>
                      <ul className="divide-y">
                        {items.map((a) => (
                          <li key={a.id} className={cn('flex items-start gap-3 px-4 py-3', selected?.id === a.id && 'bg-primary/[0.05]')}>
                            <span aria-hidden className={cn('mt-1 h-9 w-1 shrink-0 rounded-full', SEVERITY_BAR[a.severity])} />
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="font-medium">{a.summary}</span>
                                <Badge tone="outline">{t(`admin.audit.modules.${a.module}`)}</Badge>
                              </div>
                              <p className="truncate text-xs text-muted-foreground">
                                {a.actor.name} · {a.target}
                              </p>
                            </div>
                            <span className="shrink-0 pt-0.5 text-xs tabular-nums text-muted-foreground">{format(parseISO(a.at), 'HH:mm')}</span>
                            <Tooltip content={t('admin.audit.detail')} side="top">
                              <Button size="icon" variant="outline" className="size-8 shrink-0" aria-label={t('admin.audit.detailFor', { summary: a.summary })} aria-pressed={selected?.id === a.id} onClick={() => setSelectedId(a.id)}>
                                <Eye />
                              </Button>
                            </Tooltip>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                  <Pager {...pager} />
                </div>
              )}
            </section>

            <aside className="space-y-4">{selected ? <AuditDetail entry={selected} /> : null}</aside>
          </div>
        </div>
      )}
    </>
  )
}

function AuditDetail({ entry: a }: { entry: AuditEntry }) {
  const { t } = useTranslation()
  return (
    <Section
      title={t('admin.audit.detail')}
      icon={ScrollText}
      actions={<Badge tone={SEVERITY_TONE[a.severity]}>{t(`admin.audit.severities.${a.severity}`)}</Badge>}
    >
      <p className="font-semibold">{a.summary}</p>
      <p className="mb-3 text-xs text-muted-foreground">{fmtDateTime(a.at)}</p>
      <MetaList
        rows={[
          [t('admin.audit.columns.action'), t(`admin.audit.actions.${a.action}`)],
          [t('admin.audit.columns.module'), t(`admin.audit.modules.${a.module}`)],
          [t('admin.audit.columns.target'), a.target],
          [
            <span className="inline-flex items-center gap-1.5" key="a">
              <UserRound className="size-3.5" aria-hidden />
              {t('admin.audit.columns.actor')}
            </span>,
            <span key="b">
              {a.actor.name}
              <span className="block text-xs font-normal text-muted-foreground">{a.actor.email}</span>
            </span>,
          ],
          [
            <span className="inline-flex items-center gap-1.5" key="a">
              <Globe className="size-3.5" aria-hidden />
              IP
            </span>,
            <span className="font-mono text-xs" key="b">
              {a.ip}
            </span>,
          ],
          [
            <span className="inline-flex items-center gap-1.5" key="a">
              <Laptop className="size-3.5" aria-hidden />
              {t('admin.audit.device')}
            </span>,
            a.device,
          ],
        ]}
      />
      {a.changes.length ? (
        <div className="mt-3 space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('admin.audit.changes')}</p>
          <ul className="space-y-1.5">
            {a.changes.map((c) => (
              <li key={c.field} className="rounded-lg border p-2 text-sm">
                <p className="text-xs text-muted-foreground">{c.field}</p>
                <p className="flex flex-wrap items-center gap-1.5">
                  <span className="rounded bg-destructive/10 px-1.5 text-destructive line-through">{c.before}</span>
                  <ArrowRight className="size-3 text-muted-foreground" aria-hidden />
                  <span className="rounded bg-success/12 px-1.5 text-success">{c.after}</span>
                </p>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {a.link ? (
        <Button variant="outline" size="sm" className="mt-4 w-full" asChild>
          <Link to={a.link}>
            {t('admin.audit.openRecord')}
            <ArrowRight />
          </Link>
        </Button>
      ) : null}
    </Section>
  )
}
